'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

interface VideoAd {
  id: string;
  title: string;
  advertiser: string;
  video_url: string;
  image_url: string;
  thumbnail_url: string;
  thumbnail_alt: string;
  link_url: string;
  skip_after_seconds: number;
  duration_seconds: number;
  creative_type: 'video' | 'image';
  target_categories: string[] | null;
}

interface VideoAdPlayerProps {
  /** Called when the ad finishes or is skipped — main video should start */
  onAdComplete: () => void;
  placement?: 'pre-roll' | 'mid-roll' | 'post-roll';
  /** Optional: filter ads by video category */
  videoCategory?: string;
}

export default function VideoAdPlayer({ onAdComplete, placement = 'pre-roll', videoCategory }: VideoAdPlayerProps) {
  const [ad, setAd] = useState<VideoAd | null>(null);
  const [loading, setLoading] = useState(true);
  const [secondsWatched, setSecondsWatched] = useState(0);
  const [canSkip, setCanSkip] = useState(false);
  const [adEnded, setAdEnded] = useState(false);
  const impressionFiredRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const supabaseRef = useRef(createClient());

  // Ensure a stable session ID exists for this browser session
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!sessionStorage.getItem('vid_session')) {
        sessionStorage.setItem('vid_session', crypto.randomUUID());
      }
    }
  }, []);

  const recordEvent = useCallback(async (adId: string, eventType: 'impression' | 'click') => {
    const supabase = supabaseRef.current;
    try {
      await supabase.from('ad_metrics').insert({
        video_ad_id: adId,
        event_type: eventType,
        session_id: typeof window !== 'undefined' ? (sessionStorage.getItem('vid_session') ?? undefined) : undefined,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 255) : undefined,
      });

      if (eventType === 'impression') {
        await supabase.rpc('increment_ad_impressions', { ad_id: adId });
      } else {
        await supabase.rpc('increment_ad_clicks', { ad_id: adId });
      }

      // Check ad performance thresholds and notify advertiser if needed
      fetch('/api/creator-notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'check_ad_performance' }),
      }).catch(() => {/* non-critical */});
    } catch {
      // non-critical — silently fail
    }
  }, []);

  // Fetch one active ad for this placement, optionally filtered by category
  useEffect(() => {
    let cancelled = false;
    const fetchAd = async () => {
      setLoading(true);
      try {
        const supabase = supabaseRef.current;
        const now = new Date().toISOString();
        let query = supabase
          .from('video_ads')
          .select('id, title, advertiser, video_url, image_url, thumbnail_url, thumbnail_alt, link_url, skip_after_seconds, duration_seconds, creative_type, target_categories')
          .eq('ad_status', 'active')
          .eq('placement', placement)
          .or(`schedule_start.is.null,schedule_start.lte.${now}`)
          .or(`schedule_end.is.null,schedule_end.gte.${now}`)
          .limit(20);

        const { data } = await query;

        if (!cancelled && data && data.length > 0) {
          // Filter by category targeting: include ads with null target_categories (all) or matching category
          const eligible = data.filter((a: VideoAd) => {
            if (!a.target_categories || a.target_categories.length === 0) return true;
            if (!videoCategory) return true;
            return a.target_categories.includes(videoCategory);
          });

          if (eligible.length > 0) {
            const picked = eligible[Math.floor(Math.random() * eligible.length)] as VideoAd;
            setAd(picked);
          } else {
            onAdComplete();
          }
        } else if (!cancelled) {
          onAdComplete();
        }
      } catch {
        if (!cancelled) onAdComplete();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAd();
    return () => { cancelled = true; };
  }, [placement, videoCategory, onAdComplete]);

  // Fire impression once ad is shown
  useEffect(() => {
    if (ad && !loading && !impressionFiredRef.current) {
      impressionFiredRef.current = true;
      recordEvent(ad.id, 'impression');
    }
  }, [ad, loading, recordEvent]);

  // Countdown timer for skip button
  useEffect(() => {
    if (!ad || loading) return;
    timerRef.current = setInterval(() => {
      setSecondsWatched((prev) => {
        const next = prev + 1;
        if (next >= (ad.skip_after_seconds ?? 5)) setCanSkip(true);
        return next;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [ad, loading]);

  const handleSkip = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    onAdComplete();
  };

  const handleAdEnded = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setAdEnded(true);
    setTimeout(onAdComplete, 500);
  };

  const handleAdClick = () => {
    if (!ad) return;
    recordEvent(ad.id, 'click');
  };

  if (loading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-black">
        <div className="w-10 h-10 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!ad) return null;

  const skipAfter = ad.skip_after_seconds ?? 5;
  const skipCountdown = Math.max(0, skipAfter - secondsWatched);
  const isImageAd = ad.creative_type === 'image';

  return (
    <div className="absolute inset-0 bg-black">
      <div className="relative w-full h-full">
        {isImageAd ? (
          /* Image-based overlay ad */
          <img
            src={ad.image_url || ad.thumbnail_url}
            alt={ad.thumbnail_alt || ad.title}
            className="w-full h-full object-contain"
          />
        ) : (
          /* Video ad */
          <video
            ref={videoRef}
            src={ad.video_url}
            className="w-full h-full object-contain"
            autoPlay
            playsInline
            poster={ad.thumbnail_url || undefined}
            onEnded={handleAdEnded}
            onError={onAdComplete}
          />
        )}

        {/* Clickable overlay that opens ad link */}
        {ad.link_url && (
          <a
            href={ad.link_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={handleAdClick}
            className="absolute inset-0"
            aria-label={`Advertisement: ${ad.title}`}
            style={{ zIndex: 1 }}
          />
        )}

        {/* Ad label */}
        <div className="absolute top-3 left-3 z-10 flex items-center gap-2 pointer-events-none">
          <span className="text-[10px] uppercase tracking-widest text-white bg-black/70 px-2 py-0.5 rounded-sm font-bold">
            Ad
          </span>
          {ad.advertiser && (
            <span className="text-[10px] text-white/80 bg-black/60 px-2 py-0.5 rounded-sm truncate max-w-[160px]">
              {ad.advertiser}
            </span>
          )}
        </div>

        {/* Skip button / countdown */}
        <div className="absolute bottom-4 right-4 z-10">
          {canSkip ? (
            <button
              onClick={handleSkip}
              className="flex items-center gap-1.5 bg-black/80 hover:bg-black text-white text-xs font-bold px-3 py-1.5 border border-white/30 hover:border-white/60 transition-colors"
            >
              Skip Ad
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
              </svg>
            </button>
          ) : (
            <span className="bg-black/70 text-white/80 text-xs px-3 py-1.5 border border-white/20">
              {isImageAd ? (
                <button
                  onClick={handleSkip}
                  className="text-white/80 text-xs"
                >
                  Skip in {skipCountdown}s
                </button>
              ) : (
                `Skip in ${skipCountdown}s`
              )}
            </span>
          )}
        </div>

        {/* Ad ended overlay */}
        {adEnded && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-20">
            <div className="w-10 h-10 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}
